import { inferenceHttp } from '@/lib/api/http-client';

export interface OpportunitySchedule {
  organizationId: string;
  daysOfWeek: number[];
  enabled: boolean;
}

export const getOpportunitySchedule = async (): Promise<OpportunitySchedule> => {
  const response = await inferenceHttp.get({
    url: `/api/v1/opportunities/schedule`,
    responseType: 'json',
  });

  if (response.error) {
    throw new Error('Failed to fetch opportunity schedule');
  }

  return response.data as OpportunitySchedule;
};

export const updateOpportunitySchedule = async (
  data: Omit<OpportunitySchedule, 'organizationId'>,
): Promise<OpportunitySchedule> => {
  const response = await inferenceHttp.post({
    url: `/api/v1/opportunities/schedule`,
    body: {
      daysOfWeek: data.daysOfWeek,
      enabled: data.enabled,
    },
    responseType: 'json',
  });

  if (response.error) {
    throw new Error('Failed to update opportunity schedule');
  }

  return response.data as OpportunitySchedule;
};

export interface WorkspaceOpportunitySchedule extends OpportunitySchedule {
  workspaceId: string;
  hasOverride: boolean;
  generationEndDate: string | null;
}

export const listWorkspaceOpportunitySchedules = async (): Promise<
  WorkspaceOpportunitySchedule[]
> => {
  const response = await inferenceHttp.get({
    url: `/api/v1/opportunities/schedules`,
    responseType: 'json',
  });

  if (response.error) {
    throw new Error('Failed to fetch workspace opportunity schedules');
  }

  return response.data as WorkspaceOpportunitySchedule[];
};

export type UpdateWorkspaceOpportunitySchedulePayload = Omit<
  WorkspaceOpportunitySchedule,
  'organizationId' | 'hasOverride' | 'generationEndDate'
> & {
  generationEndDate?: string | null;
};

export const updateWorkspaceOpportunitySchedule = async (
  data: UpdateWorkspaceOpportunitySchedulePayload,
): Promise<WorkspaceOpportunitySchedule> => {
  const response = await inferenceHttp.post({
    url: `/api/v1/opportunities/schedules/${data.workspaceId}`,
    body: {
      daysOfWeek: data.daysOfWeek,
      enabled: data.enabled,
      generationEndDate: data.generationEndDate,
    },
    responseType: 'json',
  });

  if (response.error) {
    throw new Error('Failed to update workspace opportunity schedule');
  }

  return response.data as WorkspaceOpportunitySchedule;
};

export const removeWorkspaceOpportunitySchedule = async (workspaceId: string): Promise<void> => {
  const response = await inferenceHttp.delete({
    url: `/api/v1/opportunities/schedules/${workspaceId}`,
  });

  if (response.error) {
    throw new Error('Failed to remove workspace opportunity schedule');
  }
};
